/**
 * imageModelStats.js
 * Model picker profiles for Sales Studio plays: every Image Lab engine gets
 * 1-10 stats so users can compare them at a glance. The values are our own
 * rating from test runs, not vendor benchmarks. Model ids must stay in sync
 * with MODEL_OPTIONS (ImageLab.js) and creditCosts.json.
 */

export const MODEL_STATS = [
  { id: "photo", label: "Photoreal" },
  { id: "edit", label: "Keeps original" },
  { id: "brand", label: "Brand match" },
  { id: "text", label: "Text & type" },
  { id: "speed", label: "Speed" },
];

export const MODEL_PROFILES = [
  {
    id: "gemini-3-pro-image",
    name: "Nano Banana Pro",
    provider: "Google",
    tagline: "Understands context best, faithful to logos and real photos.",
    stats: { photo: 9, edit: 9, brand: 10, text: 9, speed: 5 },
  },
  {
    id: "flux-2-max",
    name: "FLUX.2 Max",
    provider: "Black Forest Labs · EU",
    tagline: "Highest photo quality and the most consistent edits.",
    stats: { photo: 10, edit: 9, brand: 9, text: 8, speed: 5 },
  },
  {
    id: "gpt-image-2",
    name: "GPT Image 2",
    provider: "OpenAI",
    tagline: "Sharpest text and layouts, great for ads with copy.",
    stats: { photo: 9, edit: 7, brand: 8, text: 10, speed: 3 },
  },
  {
    id: "flux-2-pro",
    name: "FLUX.2 Pro",
    provider: "Black Forest Labs · EU",
    tagline: "Photoreal at a fair price, solid for redesigns.",
    stats: { photo: 9, edit: 8, brand: 7, text: 7, speed: 7 },
  },
  {
    id: "gemini-3.1-flash-lite-image",
    name: "Nano Banana",
    provider: "Google",
    tagline: "Fast and cheap, good enough for most creatives.",
    stats: { photo: 7, edit: 7, brand: 6, text: 6, speed: 9 },
  },
  {
    id: "gpt-image-1-mini",
    name: "GPT Image Mini",
    provider: "OpenAI",
    tagline: "Cheapest option for simple visuals.",
    stats: { photo: 5, edit: 4, brand: 4, text: 6, speed: 7 },
  },
];

// Funny GIF engines: 4 s of 480p video, turned into a GIF. Grok runs on xAI,
// HeyGen and Seedance through OpenRouter.
export const GIF_MODEL_STATS = [
  { id: "fun", label: "Comic timing" },
  { id: "motion", label: "Smooth motion" },
  { id: "prompt", label: "Follows the idea" },
  { id: "speed", label: "Speed" },
];

export const GIF_MODEL_PROFILES = [
  {
    id: "heygen-video-1",
    name: "HeyGen Video",
    provider: "HeyGen",
    tagline: "Cheapest and fastest, and it lands the punchline.",
    stats: { fun: 9, motion: 8, prompt: 9, speed: 10 },
  },
  {
    id: "seedance-2.0-mini",
    name: "Seedance Mini",
    provider: "ByteDance",
    tagline: "Smoothest animation and the best comic timing.",
    // The only GIF engine that takes a set of reference images.
    references: true,
    stats: { fun: 10, motion: 10, prompt: 9, speed: 6 },
  },
  {
    id: "grok-imagine-video-1.5-lite",
    name: "Grok Imagine Lite",
    provider: "xAI",
    tagline: "Quick and solid. Plenty for a 4 second gag.",
    stats: { fun: 8, motion: 7, prompt: 7, speed: 9 },
  },
  {
    id: "grok-imagine-video-1.5",
    name: "Grok Imagine",
    provider: "xAI",
    tagline: "Cleaner motion and closer to the idea, about four times the price.",
    stats: { fun: 9, motion: 9, prompt: 9, speed: 6 },
  },
];

/** Average of all stats, one decimal. */
export const overallScore = (profile, stats = MODEL_STATS) => {
  const values = stats.map((s) => profile.stats[s.id] || 0);
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
};

/** Average of the stats a play cares about (falls back to all stats). */
export const focusScore = (profile, focus = []) => {
  const ids = focus.length ? focus : MODEL_STATS.map((s) => s.id);
  return ids.reduce((sum, id) => sum + (profile.stats[id] || 0), 0) / ids.length;
};

/**
 * Picks the "Best for this play" model (highest focus score, cheaper wins a
 * tie) and the "Best value" model (focus score per credit, among models that
 * reach at least 7 so the cheapest junk never wins).
 */
export const recommendModels = (profiles, focus, creditsById) => {
  const credits = (p) => creditsById[p.id] || Infinity;
  const best = [...profiles].sort(
    (a, b) => focusScore(b, focus) - focusScore(a, focus) || credits(a) - credits(b)
  )[0];
  const value = profiles
    .filter((p) => p.id !== best?.id && focusScore(p, focus) >= 7 && Number.isFinite(credits(p)))
    .sort((a, b) => focusScore(b, focus) / credits(b) - focusScore(a, focus) / credits(a))[0];
  return { bestId: best?.id || null, valueId: value?.id || null };
};
